import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Printer, Mail, Link as LinkIcon, QrCode, Share2, ChevronRight, ArrowLeft, ShieldAlert } from 'lucide-react';
export function SpamGuidePage() {
  const [checklist, setChecklist] = useState([false, false, false, false]);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [reflectionText, setReflectionText] = useState('');
  const [progress, setProgress] = useState(0);
  const [scenarioAnswer, setScenarioAnswer] = useState<string | null>(null);
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleCheck = (index: number) => {
    const newChecklist = [...checklist];
    newChecklist[index] = !newChecklist[index];
    setChecklist(newChecklist);
    setHasInteracted(true);
    // Update progress based on checklist and scenario (up to 50% before completion)
    calculateProgress(newChecklist, scenarioAnswer);
  };
  const handleScenario = (answer: string) => {
    setScenarioAnswer(answer);
    setHasInteracted(true);
    calculateProgress(checklist, answer);
  };
  const calculateProgress = (currentChecklist: boolean[], currentScenario: string | null) => {
    if (isCompleted) return;
    let score = 0;
    const totalItems = currentChecklist.length + 1; // checklist items + scenario
    const completedChecks = currentChecklist.filter(Boolean).length;
    score += completedChecks;
    if (currentScenario !== null) {
      score += 1;
    }
    setProgress(Math.round(score / totalItems * 50));
  };
  const handleComplete = () => {
    setIsCompleted(true);
    setProgress(100);
    // Small delay to allow render before scrolling
    setTimeout(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    }, 100);
  };
  return <div className="min-h-screen w-full bg-white text-[#1a0647]" data-id="element-5441">
      {/* Simple Navigation Bar */}
      <header className="sticky top-0 z-50 w-full bg-white border-b border-gray-200 shadow-sm" data-id="element-5442">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between" data-id="element-5443">
          <Link to="/resources" className="text-gray-600 hover:text-[#8528e2] font-manrope font-semibold text-base transition-colors flex items-center gap-2 min-h-[44px] min-w-[44px]" data-id="element-5444">
            <ArrowLeft size={20} aria-hidden="true" data-id="element-5445" />
            Back to Resources
          </Link>
          <img src="https://cdn.magicpatterns.com/uploads/8EVxVy8dkAVEdmFcPkswyr/Caveo_2026_Logo.png" alt="Caveo Logo" className="h-6 w-auto" data-id="element-5446" />
        </div>
      </header>

      <main data-id="element-5447">
        {/* 1. Module Header (Hero Block) */}
        <section className="bg-[#1a0647] text-white py-12 md:py-16 px-4 md:px-8" data-id="element-5448">
          <div className="max-w-3xl mx-auto" data-id="element-5449">
            <h1 className="font-manrope text-3xl md:text-5xl font-bold mb-4 text-white leading-tight" data-id="element-5450">
              Reducing Spam and Junk Mail
            </h1>
            <p className="font-inter text-lg md:text-xl mb-8 text-gray-200 leading-relaxed" data-id="element-5451">
              After completing this module, you'll know how to push back against
              spam across every channel using tools you already have on your
              phone and computer.
            </p>

            <div className="inline-flex items-center gap-2 bg-[#01ced1] text-[#1a0647] px-4 py-2 rounded-full font-manrope font-bold text-base mb-10 min-h-[44px]" data-id="element-5452">
              <span aria-hidden="true" data-id="element-5453">⏱️</span> 10 minutes to complete
            </div>

            <div className="space-y-3 bg-white/5 p-6 rounded-xl border border-white/10" data-id="element-5454">
              <div className="flex justify-between font-manrope font-semibold text-base" data-id="element-5455">
                <span id="progress-label" data-id="element-5456">Your progress</span>
                <span data-id="element-5457">{progress}%</span>
              </div>
              <div className="w-full bg-white/20 rounded-full h-4 overflow-hidden" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100} aria-labelledby="progress-label" data-id="element-5458">
                <div className="bg-[#214ae9] h-full transition-all duration-700 ease-out" style={{
                width: `${progress}%`
              }} data-id="element-5459" />
              </div>
            </div>
          </div>
        </section>

        {/* 2. The Hook (Story Block) */}
        <section className="bg-[#eae6dc] py-12 md:py-16 px-4 md:px-8" data-id="element-5460">
          <div className="max-w-3xl mx-auto flex flex-col sm:flex-row gap-6 items-start" data-id="element-5461">
            <div className="flex-shrink-0 w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm text-3xl border border-gray-200" aria-hidden="true" data-id="element-5462">
              📱
            </div>
            <div className="flex-1 space-y-4" data-id="element-5463">
              <p className="font-inter text-gray-700 text-lg leading-relaxed" data-id="element-5464">
                David's phone buzzed for the fifth time during dinner. Another
                "urgent package delivery" text with a suspicious link. His inbox
                was overflowing with newsletters he never signed up for, and his
                physical mailbox was stuffed with credit card offers. He felt
                like he was constantly swatting away digital mosquitoes, but
                they just kept coming back.
              </p>
              <p className="font-inter text-[#1a0647] text-xl font-bold leading-relaxed" data-id="element-5465">
                In this module, you'll learn exactly how to stop the noise and
                take back control of your inbox, phone, and mailbox using
                simple, built-in tools.
              </p>
            </div>
          </div>
        </section>

        {/* 3. Concept Cards (One Idea Per Card) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-5466">
          <div className="max-w-4xl mx-auto" data-id="element-5467">
            <h2 className="sr-only" data-id="element-5468">Key Concepts</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5469">
              {/* Card 1 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5470">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-5471">
                  Training Your Email Filter
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-5472">
                  Your email provider has built-in tools to report suspicious
                  messages. Every time you click "Report Spam" or "Report
                  Phishing" instead of just deleting an email, you train the
                  system's AI to catch similar junk before it ever reaches your
                  inbox.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-5473">
                  Think of this like teaching a guard dog which strangers to
                  bark at. If you just quietly throw the mail away, the dog
                  never learns.
                </p>
              </div>

              {/* Card 2 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5474">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-5475">
                  Stopping Text Spam
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-5476">
                  You can report spam texts directly to your carrier by
                  forwarding the message to 7726 (which spells "SPAM" on your
                  keypad). Both iPhones and Androids also have built-in features
                  to filter unknown senders into a separate folder so they don't
                  interrupt your day.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-5477">
                  Think of 7726 like a direct hotline to the digital bouncer at
                  your phone company.
                </p>
              </div>

              {/* Card 3 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5478">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-5479">
                  Blocking Scam Calls
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-5480">
                  The National Do Not Call Registry stops legitimate
                  telemarketers, but scammers ignore it. To stop them, you need
                  to activate the free spam-blocking app provided by your
                  carrier (like AT&T ActiveArmor, Verizon Call Filter, or
                  T-Mobile Scam Shield).
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-5481">
                  Think of the Do Not Call list as a "No Trespassing" sign, and
                  your carrier's app as the actual locked gate.
                </p>
              </div>

              {/* Card 4 */}
              <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-5482">
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold mb-4" data-id="element-5483">
                  Halting Physical Junk Mail
                </h3>
                <p className="font-inter text-gray-700 text-base mb-6 leading-relaxed" data-id="element-5484">
                  You can significantly reduce unsolicited catalogs, credit card
                  offers, and marketing mailers by registering with DMAchoice, a
                  mail preference service run by the Data and Marketing
                  Association.
                </p>
                <p className="font-inter text-[#8528e2] text-base italic font-medium" data-id="element-5485">
                  Think of DMAchoice as turning off the main water valve instead
                  of trying to catch every drip with a bucket.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 4. Practice Activity (Interactive Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white border-t border-gray-100" data-id="element-5486">
          <div className="max-w-3xl mx-auto" data-id="element-5487">
            <h2 className="font-manrope text-[#1a0647] text-3xl md:text-4xl font-bold mb-6" data-id="element-5488">
              Try It Now
            </h2>
            <p className="font-inter text-gray-700 text-lg mb-10 leading-relaxed" data-id="element-5489">
              Let's set up your defenses right now. Tap each step as you
              complete it on your devices.
            </p>

            <div className="space-y-4 mb-12" role="group" aria-label="Preparation Checklist" data-id="element-5490">
              {["Find the 'Report Spam' or 'Report Phishing' button in your email app so you know where it is.", "Save the number 7726 in your phone's contacts as 'Report SPAM'.", "Turn on 'Filter Unknown Senders' (iPhone) or 'Spam Protection' (Android) in your messaging app settings.", "Download and activate your carrier's free spam-blocking app (ActiveArmor, Call Filter, or Scam Shield)."].map((item, i) => <button key={i} onClick={() => handleCheck(i)} className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 flex items-start gap-5 min-h-[44px] ${checklist[i] ? 'border-[#01ced1] bg-[#01ced1]/5 shadow-sm' : 'border-gray-200 bg-[#f8f8f8] hover:border-[#8528e2]/40 hover:bg-white'}`} aria-pressed={checklist[i]} data-id="element-5491">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 mt-0.5 transition-colors ${checklist[i] ? 'bg-[#01ced1] border-[#01ced1] text-white' : 'border-gray-300 text-transparent bg-white'}`} data-id="element-5492">
                    <CheckCircle2 size={20} aria-hidden="true" data-id="element-5493" />
                  </div>
                  <span className={`font-inter text-lg leading-relaxed ${checklist[i] ? 'text-[#1a0647] font-semibold' : 'text-gray-700'}`} data-id="element-5494">
                    {item}
                  </span>
                </button>)}
            </div>

            {/* Scenario Choice */}
            <div className="bg-[#f8f8f8] p-8 rounded-2xl border border-gray-200" data-id="element-5495">
              <div className="flex items-center gap-3 mb-6" data-id="element-5496">
                <ShieldAlert size={28} className="text-[#8528e2]" aria-hidden="true" data-id="element-5497" />
                <h3 className="font-manrope text-[#1a0647] text-2xl font-bold" data-id="element-5498">
                  Quick Check
                </h3>
              </div>
              <p className="font-inter text-gray-700 text-lg mb-6 leading-relaxed" data-id="element-5499">
                You receive a text message from an unknown number saying your
                Netflix account has been suspended, with a link to "update your
                payment info." What is the best way to handle this?
              </p>

              <div className="space-y-3" data-id="element-5500">
                <button onClick={() => handleScenario('A')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'A' ? 'border-[#214ae9] bg-[#214ae9]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-5501">
                  A: Reply "STOP" to unsubscribe from the messages.
                </button>
                <button onClick={() => handleScenario('B')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'B' ? 'border-[#01ced1] bg-[#01ced1]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-5502">
                  B: Forward the message to 7726, then delete and report it as
                  junk.
                </button>
                <button onClick={() => handleScenario('C')} className={`w-full text-left p-4 rounded-xl border-2 transition-colors min-h-[44px] font-inter text-base ${scenarioAnswer === 'C' ? 'border-[#214ae9] bg-[#214ae9]/5 text-[#1a0647]' : 'border-gray-200 bg-white hover:border-gray-300 text-gray-700'}`} data-id="element-5503">
                  C: Click the link just to see if it's a real Netflix page, but
                  don't enter any info.
                </button>
              </div>

              {scenarioAnswer === 'B' && <div className="mt-6 p-4 bg-[#01ced1]/10 border-l-4 border-[#01ced1] rounded-r-lg animate-in fade-in" data-id="element-5504">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-5505">
                    That's right! Forwarding to 7726 alerts your carrier, and
                    reporting it as junk trains your phone's filters. Never
                    reply to or click links in suspicious texts.
                  </p>
                </div>}
              {scenarioAnswer === 'A' && <div className="mt-6 p-4 bg-[#214ae9]/10 border-l-4 border-[#214ae9] rounded-r-lg animate-in fade-in" data-id="element-5506">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-5507">
                    Not quite — replying "STOP" to a scammer actually confirms
                    your number is active, which will lead to more spam. Forward
                    it to 7726 instead.
                  </p>
                </div>}
              {scenarioAnswer === 'C' && <div className="mt-6 p-4 bg-[#214ae9]/10 border-l-4 border-[#214ae9] rounded-r-lg animate-in fade-in" data-id="element-5508">
                  <p className="font-inter text-[#1a0647] text-base font-medium" data-id="element-5509">
                    Actually, clicking the link is dangerous. It can confirm
                    your number is active or even install malware on your
                    device. Forward it to 7726 instead.
                  </p>
                </div>}
            </div>
          </div>
        </section>

        {/* 5. Honesty Gate (Pre-Commitment Block) */}
        <section className="py-8 px-4 md:px-8 bg-white" data-id="element-5510">
          <div className="max-w-3xl mx-auto" data-id="element-5511">
            <div className="border-l-4 border-[#8528e2] bg-[#8528e2]/5 p-6 md:p-8 rounded-r-xl mb-8" data-id="element-5512">
              <p className="font-inter text-[#1a0647] text-lg leading-relaxed font-medium" data-id="element-5513">
                Before you confirm: this step matters for your family's
                protection. Please only mark it complete if you actually did it.
                It's completely okay to come back to this later.
              </p>
            </div>

            {hasInteracted && !isCompleted && <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5514">
                <button onClick={handleComplete} className="bg-[#8528e2] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#b063ff] transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center w-full md:w-auto shadow-md" data-id="element-5515">
                  I completed this step
                </button>
              </div>}
          </div>
        </section>

        {/* 6. Reflection Prompt (Personal Connection Block) */}
        <section className="py-16 md:py-24 px-4 md:px-8 bg-white" data-id="element-5516">
          <div className="max-w-3xl mx-auto" data-id="element-5517">
            <div className="bg-[#f8f8f8] border border-gray-200 p-8 md:p-10 rounded-2xl shadow-sm" data-id="element-5518">
              <div className="inline-block bg-[#eae6dc] text-[#1a0647] font-manrope font-bold px-4 py-1.5 rounded-md text-sm mb-6 uppercase tracking-wider" data-id="element-5519">
                Reflect for a Moment
              </div>
              <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold mb-8 leading-tight" data-id="element-5520">
                Which type of spam bothers you the most, and which tool will you
                set up first to stop it?
              </h3>
              <textarea value={reflectionText} onChange={e => setReflectionText(e.target.value)} placeholder="Take a moment to think about this..." className="w-full bg-white border border-gray-300 rounded-xl p-5 font-inter text-gray-700 text-lg min-h-[160px] focus:outline-none focus:ring-2 focus:ring-[#8528e2] focus:border-transparent resize-y shadow-inner" aria-label="Reflection input" data-id="element-5521" />
              <p className="font-inter text-gray-500 text-sm mt-4" data-id="element-5522">
                This is just for you — your response isn't stored or shared.
              </p>
            </div>
          </div>
        </section>

        {/* 7. Takeaway Block (Action Anchor) */}
        <section className="bg-[#1a0647] text-white py-20 px-4 md:px-8" data-id="element-5523">
          <div className="max-w-3xl mx-auto text-center" data-id="element-5524">
            <p className="font-inter text-2xl md:text-3xl font-bold leading-relaxed mb-12" data-id="element-5525">
              The single most important action you can take today is to register
              your number on the Do Not Call Registry and turn on your carrier's
              spam blocking app.
            </p>

            <div className="flex flex-wrap justify-center gap-4 md:gap-6" data-id="element-5526">
              <button onClick={() => window.print()} className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-5527">
                <Printer size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-5528" />
                <span className="font-manrope text-sm font-semibold" data-id="element-5529">
                  Print
                </span>
              </button>
              <a href="mailto:?subject=Helpful guide on reducing spam&body=I found this guide on reducing spam and junk mail really helpful. You should check it out." className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5 no-underline text-white" data-id="element-5530">
                <Mail size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-5531" />
                <span className="font-manrope text-sm font-semibold" data-id="element-5532">
                  Email to myself
                </span>
              </a>
              <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5" data-id="element-5533">
                <LinkIcon size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-5534" />
                <span className="font-manrope text-sm font-semibold" data-id="element-5535">
                  Copy link
                </span>
              </button>
              <button title="Coming soon" className="flex flex-col items-center justify-center gap-3 bg-white/10 hover:bg-white/20 p-5 rounded-xl transition-colors min-h-[88px] min-w-[100px] border border-white/5 cursor-not-allowed opacity-70" data-id="element-5536">
                <QrCode size={28} className="text-[#01ced1]" aria-hidden="true" data-id="element-5537" />
                <span className="font-manrope text-sm font-semibold" data-id="element-5538">
                  QR code
                </span>
              </button>
            </div>
          </div>
        </section>

        {/* 8. Completion Confirmation */}
        {isCompleted && <section className="bg-[#01ced1] py-16 md:py-24 px-4 md:px-8 animate-in fade-in slide-in-from-bottom-8 duration-1000" data-id="element-5539">
            <div className="max-w-3xl mx-auto text-center" data-id="element-5540">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-8 shadow-lg" data-id="element-5541">
                <CheckCircle2 size={40} className="text-[#01ced1]" aria-hidden="true" data-id="element-5542" />
              </div>
              <h2 className="font-manrope text-[#1a0647] text-3xl md:text-5xl font-bold mb-6 leading-tight" data-id="element-5543">
                Nice work. The Spam Reduction module is complete. Your inbox is
                a little quieter today.
              </h2>

              <div className="bg-white/30 p-8 rounded-2xl mt-10 max-w-lg mx-auto backdrop-blur-sm border border-white/40 shadow-sm" data-id="element-5544">
                <p className="font-inter text-[#1a0647] text-xl font-bold mb-6" data-id="element-5545">
                  Know someone who could use this?
                </p>
                <a href="mailto:?subject=Helpful guide on reducing spam&body=I found this guide on reducing spam and junk mail really helpful. You should check it out." className="bg-[#1a0647] text-white font-manrope font-bold text-lg px-8 py-4 rounded-xl hover:bg-[#2a0a6b] transition-colors min-h-[44px] inline-flex items-center justify-center gap-3 shadow-md w-full sm:w-auto no-underline" data-id="element-5546">
                  <Share2 size={20} aria-hidden="true" data-id="element-5547" />
                  Send to a family member
                </a>
              </div>

              <div className="mt-16 text-left max-w-2xl mx-auto" data-id="element-5548">
                <Link to="/resources" className="block bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all group no-underline border-2 border-transparent hover:border-[#8528e2]/50" data-id="element-5549">
                  <p className="font-manrope text-[#8528e2] font-bold text-sm uppercase tracking-wider mb-3" data-id="element-5550">
                    What's Next
                  </p>
                  <div className="flex items-center justify-between" data-id="element-5551">
                    <h3 className="font-manrope text-[#1a0647] text-2xl md:text-3xl font-bold" data-id="element-5552">
                      Back to Resources
                    </h3>
                    <div className="w-12 h-12 rounded-full bg-[#f8f8f8] flex items-center justify-center group-hover:bg-[#8528e2] group-hover:text-white transition-colors text-[#1a0647] shadow-sm" data-id="element-5553">
                      <ChevronRight size={24} aria-hidden="true" data-id="element-5554" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </section>}
      </main>
    </div>;
}